import { forwardRef } from "react";
import i18nPackage from "../../i18n/i18n";
import { BasicStep } from "../components/basic-step";
import {
	BaseSubstepProps,
	BasicSubstep,
	BasicSubstepDescription,
	BasicSubstepHeader,
	BasicSubstepTitle,
	getBasicSubstepIcon,
} from "../components/basic-substep";
import styles from "./index.module.css";
import {
	CameraPreview,
	CameraPreviewProps,
	Waveform,
	AudioErrorType,
	WaveformProps,
	DeviceDropdown,
	DeviceDropdownProps,
} from "../components/media-components";
import { Box, Typography } from "@mui/material";
import { CoreButton, CoreSpinner } from "../../core";
import { DeviceSetupKey } from "../before-quiz";
import Guide from "../components/guide";

type CameraSubstepProps = BaseSubstepProps & {
	slotProps: {
		cameraPreview: CameraPreviewProps;
		dropdown: Omit<DeviceDropdownProps, "devices" | "type"> & {
			devices: Omit<MediaDeviceInfo, "toJSON">[] | undefined;
		};
	};
	nonChromium?: {
		onSelectCameraClick: () => void;
		onSelectCameraDisabled?: boolean;
	};
};

export function CameraSubstep({ status, slotProps, rootRef, nonChromium }: CameraSubstepProps) {
	const { cameraError, isVideoLoading } = slotProps.cameraPreview;
	const { devices, onChange, selectedDeviceIds, container } = slotProps.dropdown;
	const statusKey = status === "started" ? "unstarted" : status === "failed" ? (cameraError ?? "unknown") : status;

	const showSelectCameraButton = nonChromium && (status === "unstarted" || status === "failed");
	const showCameraPreview = !nonChromium || status === "completed";
	return (
		<BasicSubstep ref={rootRef}>
			<div className={styles.mediaContainer}>
				<div className={styles.mediaTopRow}>
					{getBasicSubstepIcon(status)}
					<div className={styles.mediaLeftRegion}>
						<div className={styles.mediaHeaderWrapper}>
							<BasicSubstepTitle
								title={i18nPackage.t(`beforeQuiz.deviceSetup.videoRecordingMode.title.${statusKey}`)}
							/>
							<BasicSubstepDescription
								description={i18nPackage.t(`beforeQuiz.deviceSetup.videoRecordingMode.description.${statusKey}`)}
							/>
						</div>
						{devices && status !== "unstarted" && (
							<DeviceDropdown
								devices={devices}
								onChange={onChange}
								selectedDeviceIds={selectedDeviceIds}
								container={container}
								type="camera"
								disabled={isVideoLoading && !cameraError}
							/>
						)}
					</div>
				</div>
				{showSelectCameraButton && (
					<Box className={styles.mediaButtonWrapper}>
						<CoreButton
							className={styles.mediaButton}
							onClick={nonChromium?.onSelectCameraClick}
							disabled={nonChromium?.onSelectCameraDisabled}
						>
							{i18nPackage.t("beforeQuiz.deviceSetup.videoRecordingMode.selectWebcam")}
						</CoreButton>
					</Box>
				)}
				<div className={styles.videoContainer} style={{ display: showCameraPreview ? "" : "none" }}>
					<CameraPreview {...slotProps.cameraPreview} />
				</div>
			</div>
			{status === "failed" && <VideoAudioGuide type="videoRecordingMode" />}
		</BasicSubstep>
	);
}

type AudioSubstepProps = BaseSubstepProps & {
	audioError?: AudioErrorType;
	slotProps: {
		waveform: WaveformProps;
		dropdown: Omit<DeviceDropdownProps, "devices" | "type"> & {
			devices: Omit<MediaDeviceInfo, "toJSON">[] | undefined;
		};
	};
	nonChromium?: {
		onSelectMicrophoneClick: () => void;
		onSelectMicrophoneDisabled?: boolean;
	};
};

export function AudioSubstep({ status, audioError, slotProps, rootRef, nonChromium }: AudioSubstepProps) {
	const { devices, onChange, selectedDeviceIds, container } = slotProps.dropdown;
	const { volume, height } = slotProps.waveform;
	const statusKey =
		status === "started"
			? "unstarted"
			: status === "failed" && audioError !== "noAudio"
				? (audioError ?? "unknown")
				: status;
	const showSelectAudioButton = nonChromium && (status === "unstarted" || status === "failed");
	const showMicrophoneWaveform = status === "completed" || status === "started";
	return (
		<BasicSubstep ref={rootRef}>
			<div className={styles.mediaContainer}>
				<div className={styles.mediaTopRow}>
					{getBasicSubstepIcon(status)}
					<div className={styles.mediaLeftRegion}>
						<div className={styles.mediaHeaderWrapper}>
							<BasicSubstepTitle
								title={i18nPackage.t(`beforeQuiz.deviceSetup.audioRecordingMode.title.${statusKey}`)}
							/>
							<BasicSubstepDescription
								description={i18nPackage.t(`beforeQuiz.deviceSetup.audioRecordingMode.description.${statusKey}`)}
							/>
						</div>
						{devices && status !== "unstarted" && (
							<DeviceDropdown
								devices={devices}
								onChange={onChange}
								selectedDeviceIds={selectedDeviceIds}
								container={container}
								type="audio"
							/>
						)}
					</div>
				</div>
				{showSelectAudioButton && (
					<Box className={styles.mediaButtonWrapper}>
						<CoreButton
							onClick={nonChromium?.onSelectMicrophoneClick}
							className={styles.mediaButton}
							disabled={nonChromium?.onSelectMicrophoneDisabled}
						>
							{i18nPackage.t("beforeQuiz.deviceSetup.audioRecordingMode.selectMicrophone")}
						</CoreButton>
					</Box>
				)}
				{showMicrophoneWaveform && (
					<div className={styles.audioWaveContainer}>
						{audioError === "noAudio" && (
							<div className={styles.noAudioWarning}>
								<Typography>
									{i18nPackage.t("beforeQuiz.deviceSetup.audioRecordingMode.description.noAudio")}
								</Typography>
							</div>
						)}
						{status === "completed" && <Waveform volume={volume} height={height} />}
						{status === "started" && (
							<Box width={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"} height={"100%"}>
								<CoreSpinner size={40} version="v2" />
							</Box>
						)}
					</div>
				)}
			</div>
			{status === "failed" && <VideoAudioGuide type="audioRecordingMode" />}
		</BasicSubstep>
	);
}

function VideoAudioGuide({ type }: { type: Extract<DeviceSetupKey, "audioRecordingMode" | "videoRecordingMode"> }) {
	return (
		<Guide
			description={i18nPackage.t(`beforeQuiz.deviceSetup.${type}.guideDescription`)}
			iframeTitle={i18nPackage.t(`beforeQuiz.deviceSetup.${type}.videoTitle`)}
			src={"https://demo.video.yuja.com/V/Video?v=14648831&node=63114343&a=103235564"}
			title={i18nPackage.t(`beforeQuiz.deviceSetup.${type}.guideTitle`)}
			className={styles.videoAudioGuide}
		/>
	);
}

type ScreenShareSubstepProps = BaseSubstepProps & {
	isMac: boolean;
	onShareScreen: () => void;
};

export function ScreenShareSubstep({ status, isMac, onShareScreen, rootRef }: ScreenShareSubstepProps) {
	const statusKey = status === "started" ? "unstarted" : status;
	return (
		<BasicSubstep ref={rootRef}>
			<div className={styles.screenShareSubstep}>
				<BasicSubstepHeader
					title={i18nPackage.t(`beforeQuiz.deviceSetup.recordScreen.title.${statusKey}`)}
					description={
						status === "failed" && isMac
							? i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.description.failedMac")
							: i18nPackage.t(`beforeQuiz.deviceSetup.recordScreen.description.${statusKey}`)
					}
					status={status}
				/>
				{(status === "failed" || status === "unstarted") && (
					<CoreButton className={styles.mediaButton} onClick={onShareScreen}>
						{status === "failed"
							? i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.tryAgain")
							: i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.selectScreen")}
					</CoreButton>
				)}
			</div>
			{status === "failed" && <ScreenshareGuide isMac={isMac} />}
		</BasicSubstep>
	);
}

function ScreenshareGuide({ isMac }: { isMac: boolean }) {
	return (
		<Guide
			description={
				isMac
					? i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.macGuide")
					: i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.windowGuide")
			}
			iframeTitle={i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.videoTitle")}
			src={
				isMac
					? "https://demo.video.yuja.com/V/Video?v=14648940&node=63114500&a=160471175"
					: "https://demo.video.yuja.com/V/Video?v=14648812&node=63114312&a=139696381"
			}
			title={i18nPackage.t("beforeQuiz.deviceSetup.recordScreen.guideTitle")}
		/>
	);
}

const DeviceSetupStep = forwardRef<HTMLDivElement, { children: React.ReactNode; substeps: DeviceSetupKey[] }>(
	function DeviceSetupStep({ children, substeps }, ref) {
		const deviceLabels: string[] = [];

		if (substeps.includes("videoRecordingMode")) {
			deviceLabels.push(i18nPackage.t("beforeQuiz.deviceSetup.devices.camera"));
		}
		if (substeps.includes("audioRecordingMode")) {
			deviceLabels.push(i18nPackage.t("beforeQuiz.deviceSetup.devices.microphone"));
		}
		if (substeps.includes("recordScreen")) {
			deviceLabels.push(i18nPackage.t("beforeQuiz.deviceSetup.devices.screen"));
		}

		// Fallback in case substeps is empty
		if (deviceLabels.length === 0) {
			deviceLabels.push(i18nPackage.t("beforeQuiz.deviceSetup.devices.camera"));
		}

		const lf = new Intl.ListFormat(i18nPackage.language, { style: "long", type: "conjunction" });
		return (
			<BasicStep
				description={i18nPackage.t("beforeQuiz.deviceSetup.description", { devices: lf.format(deviceLabels) })}
				ref={ref}
			>
				{children}
			</BasicStep>
		);
	},
);

export default DeviceSetupStep;
